import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dataGridCustomizations } from "../../../theme/customizations";
import AppTheme from "../../../theme/AppTheme";
import {
  CssBaseline,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PaginationButtons from "../../../components/Pagination/PaginationButtons";
import { notifyError } from "../../../components/ToastContainer/Toast/Toast";
import { getCampaigns } from "../../../service/BuyerConfiguration/CampaignManagement/campaignService";
import GenericModal from "../../../components/Modal/GenericModal";
import CreateCampaignModal from "./Fragments/CreateCampaignModal";

const PromoCodeManagement = (props: { disableCustomTheme?: boolean }) => {
  const xThemeComponents = {
    ...dataGridCustomizations,
  };

  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);

  const getOrdinalSuffix = (day: number): string => {
    const j = day % 10;
    const k = day % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  const formatCreatedAt = (dateString: string): string => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    const month = date.toLocaleString("en-GB", { month: "long" });
    const year = date.getFullYear();
    return `${day}${suffix} ${month} ${year}`;
  };

  const fetchCampaigns = async (pageToFetch: number = 1) => {
    try {
      setLoading(true);
      const response = await getCampaigns(pageToFetch, limit);
      const campaigns =
        response?.data?.campaigns ||
        response?.campaigns ||
        response?.data?.codes ||
        response?.codes ||
        [];
      const totalItems =
        response?.data?.total ||
        response?.total ||
        0;

      setRows(Array.isArray(campaigns) ? campaigns : []);
      setTotal(totalItems);
      setTotalPages(Math.max(1, Math.ceil(totalItems / limit)));
    } catch (error: any) {
      notifyError(error?.response?.data?.message || "Failed to load campaigns");
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const columns: GridColDef[] = [
    { field: "title", headerName: "Title", minWidth: 220, flex: 1.2 },
    {
      field: "campaign_key",
      headerName: "Campaign Key",
      minWidth: 220,
      flex: 1,
    },
    {
      field: "created_by",
      headerName: "Created By",
      minWidth: 220,
      flex: 1,
    },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => formatCreatedAt(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.8,
      renderCell: (params) => {
        const statusValue = (params.value || "").toString();
        const normalized = statusValue.toLowerCase();
        const isActive = normalized === "active";
        const label =
          statusValue.charAt(0).toUpperCase() + statusValue.slice(1).toLowerCase();
        return (
          <Chip
            label={label}
            size="small"
            color={isActive ? "success" : "default"}
            variant="outlined"
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 150,
      flex: 0.7,
      sortable: false,
      filterable: false,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const statusRaw = (params?.row?.status ?? "").toString();
        const normalizedStatus = statusRaw.trim().toLowerCase();
        const isEditDisabled =
          normalizedStatus === "expired" || normalizedStatus === "revoked";

        return (
          <Box display="flex" alignItems="center" sx={{ marginTop: 0.7 }}>
            <IconButton
              color="primary"
              disabled={isEditDisabled}
              onClick={() => {
                if (isEditDisabled) return;
                setSelectedCampaign(params.row);
                setModalMode("edit");
                setOpenCreateModal(true);
              }}
              sx={{
                opacity: isEditDisabled ? 0.45 : 1,
                pointerEvents: isEditDisabled ? "none" : "auto",
              }}
              aria-label="Edit campaign"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          <IconButton
            color="primary"
            sx={{ ml: 1 }}
            onClick={() => navigate(`/campaign-management/${params.row.id}`)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="primary"
            sx={{ ml: 1 }}
            onClick={() => navigate(`/campaign-management/${params.row.id}/analytics`)}
            aria-label="Campaign analytics dashboard"
          >
            <DashboardRoundedIcon fontSize="small" />
          </IconButton>
          </Box>
        );
      },
    },
  ];

  const CustomPagination = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        p: 2,
        background: "#f5f6fa",
      }}
    >
      <PaginationButtons
        count={totalPages}
        page={page}
        onChange={(value) => setPage(value)}
        totalItems={total}
        itemsPerPage={limit}
      />
    </Box>
  );

  return (
    <>
      <AppTheme {...props} themeComponents={xThemeComponents}>
        <CssBaseline enableColorScheme />
        <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
          <Typography
            component="h2"
            variant="h6"
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              rowGap: 1.5,
            }}
          >
            Campaign Management List
            <Button
              variant="contained"
              color="primary"
              sx={{ minWidth: { xs: "100%", sm: "auto" } }}
              onClick={() => {
                setSelectedCampaign(null);
                setModalMode("create");
                setOpenCreateModal(true);
              }}
            >
              Create Campaign
            </Button>
          </Typography>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box sx={{ minWidth: 980 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                disableColumnMenu
                loading={loading}
                paginationMode="server"
                rowCount={total}
                pageSizeOptions={[10]}
                paginationModel={{ page: page - 1, pageSize: limit }}
                slots={{ pagination: CustomPagination }}
                sx={{
                  height: { xs: 500, sm: 550 },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: (theme) => theme.palette.background.paper,
                  },

                }}
              />
            </Box>
          </Box>
        </Box>
        <GenericModal
          open={openCreateModal}
          onClose={() => {
            setOpenCreateModal(false);
            setSelectedCampaign(null);
          }}
          title={modalMode === "edit" ? "Edit Campaign" : "Create Campaign"}
          maxWidth="md"
        >
          <CreateCampaignModal
            mode={modalMode}
            selectedCampaign={selectedCampaign}
            onClose={() => {
              setOpenCreateModal(false);
              setSelectedCampaign(null);
            }}
            onCreated={() => fetchCampaigns(page)}
          />
        </GenericModal>
      </AppTheme>
    </>
  );
};

export default PromoCodeManagement;
